import type { ReportData, Status } from '@/lib/types';

const scoreColor: Record<Status, string> = {
  ok: 'text-ok-deep',
  warn: 'text-warn-deep',
  crit: 'text-crit-deep',
};

const alertBadge: Record<Status, string> = {
  ok: 'bg-ok-tint text-ok-deep',
  warn: 'bg-warn-tint text-warn-deep',
  crit: 'bg-crit-tint text-crit-deep',
};

const alertLabel: Record<Status, string> = {
  ok: 'ÓPTIMO',
  warn: 'ATENCIÓN',
  crit: 'CRÍTICO',
};

interface ResumenTabProps {
  report: ReportData;
}

export function ResumenTab({ report }: ResumenTabProps) {
  const c = report.biomarkerCounts;
  const deltaSign = report.bioAgeDelta < 0 ? '−' : '+';
  const deltaValue = Math.abs(report.bioAgeDelta).toFixed(1);
  const deltaStatus: Status = report.bioAgeDelta < 0 ? 'ok' : 'crit';
  const critCount = report.alerts.filter((a) => a.severity === 'crit').length;

  return (
    <div className="flex flex-col gap-3.5">
      {/* Score + ages */}
      <section className="glass-panel grid grid-cols-4 gap-6 px-7 py-7 max-[760px]:grid-cols-2 max-[760px]:gap-5 max-[760px]:p-5">
        <div className="flex flex-col gap-1">
          <span
            className={`text-[64px] font-bold tracking-[-0.04em] tnum leading-none ${scoreColor[report.scoreStatus]}`}
          >
            {report.optimalScore}
          </span>
          <span className="text-[11px] font-semibold tracking-[0.06em] uppercase text-muted mt-1">
            <span className="font-light tracking-[-0.005em] normal-case text-[12px] mr-1">
              Optimal
            </span>
            SCORE
          </span>
        </div>
        <ScoreCell label="Edad biológica" value={report.bioAge.toFixed(1)} />
        <ScoreCell label="Edad cronológica" value={report.chronoAge.toString()} />
        <ScoreCell
          label="Diferencia"
          value={`${deltaSign}${deltaValue}`}
          suffix="años"
          tone={deltaStatus}
        />
      </section>

      {/* Priority alerts */}
      <section className="flex flex-col gap-2.5">
        <header className="flex items-baseline justify-between gap-3">
          <h3 className="text-[15px] font-semibold tracking-[-0.01em] m-0">
            Alertas prioritarias
          </h3>
          <p className="text-[12px] text-tertiary m-0">
            {critCount} biomarcador{critCount === 1 ? '' : 'es'} requiere{critCount === 1 ? '' : 'n'}{' '}
            atención clínica
          </p>
        </header>
        <ul className="list-none m-0 p-0 grid grid-cols-3 gap-2 max-[880px]:grid-cols-2 max-[760px]:grid-cols-1">
          {report.alerts.map((a) => (
            <li
              key={a.code}
              className="glass-panel px-4 py-3.5 flex flex-col gap-2 min-w-0"
            >
              <header className="flex items-center justify-between gap-2">
                <span
                  className={`w-4 h-4 rounded-full grid place-items-center text-[11px] font-bold shrink-0 ${alertBadge[a.severity]}`}
                  aria-hidden="true"
                >
                  !
                </span>
                <span
                  className={`text-[10px] font-semibold tracking-[0.06em] px-2 py-1 rounded-sm ${alertBadge[a.severity]} shrink-0`}
                >
                  {alertLabel[a.severity]}
                </span>
              </header>
              <div className="text-[13.5px] font-semibold tracking-[-0.005em] leading-[1.35] truncate">
                {a.title}{' '}
                <span className="text-tertiary font-normal">— {a.value}</span>
              </div>
              <p className="text-[12px] text-tertiary leading-[1.5] m-0">
                {a.description}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {/* Distribution */}
      <section className="glass-panel px-7 py-6 max-[760px]:p-5">
        <header className="flex items-baseline justify-between gap-3 mb-4">
          <h3 className="text-[15px] font-semibold tracking-[-0.01em] m-0">
            Distribución de biomarcadores
          </h3>
          <p className="text-[12px] text-tertiary tnum m-0">
            {c.total} biomarcadores evaluados
          </p>
        </header>
        <div className="flex h-1.5 gap-px rounded-full overflow-hidden mb-5">
          <div
            className="h-full bg-ok first:rounded-l-full last:rounded-r-full"
            style={{ width: `${(c.ok / c.total) * 100}%` }}
            aria-label={`Óptimos ${c.ok}`}
          />
          <div
            className="h-full bg-warn first:rounded-l-full last:rounded-r-full"
            style={{ width: `${(c.warn / c.total) * 100}%` }}
            aria-label={`Mejorables ${c.warn}`}
          />
          <div
            className="h-full bg-crit first:rounded-l-full last:rounded-r-full"
            style={{ width: `${(c.crit / c.total) * 100}%` }}
            aria-label={`Prioritarios ${c.crit}`}
          />
        </div>
        <div className="grid grid-cols-3 gap-2 max-[760px]:grid-cols-1">
          <DistributionCell label="Óptimos" count={c.ok} total={c.total} tone="ok" />
          <DistributionCell label="Mejorables" count={c.warn} total={c.total} tone="warn" />
          <DistributionCell label="Prioritarios" count={c.crit} total={c.total} tone="crit" />
        </div>
      </section>
    </div>
  );
}

interface ScoreCellProps {
  label: string;
  value: string;
  suffix?: string;
  tone?: Status;
}

function ScoreCell({ label, value, suffix, tone }: ScoreCellProps) {
  const valueClass = tone ? scoreColor[tone] : 'text-primary';
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold tracking-[0.06em] uppercase text-muted">
        {label}
      </span>
      <span className={`text-[40px] font-bold tracking-[-0.025em] tnum leading-none ${valueClass}`}>
        {value}
      </span>
      {suffix && (
        <span className="text-[11px] text-tertiary mt-0.5">{suffix}</span>
      )}
    </div>
  );
}

interface DistributionCellProps {
  label: string;
  count: number;
  total: number;
  tone: Status;
}

const dotColor: Record<Status, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  crit: 'bg-crit',
};

function DistributionCell({ label, count, total, tone }: DistributionCellProps) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex flex-col gap-1.5 bg-transparent border border-line rounded-md px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted">
        <span className={`w-1.5 h-1.5 rounded-full ${dotColor[tone]}`} aria-hidden="true" />
        {label}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className={`text-[24px] font-bold tracking-[-0.025em] tnum leading-none ${scoreColor[tone]}`}>
          {count}
        </span>
        <span className="text-[11px] text-tertiary tnum">{pct}%</span>
      </div>
    </div>
  );
}
